/**
 * Groups API - Group Detail
 *
 * GET /v1/groups/:groupId
 */

import { z } from 'zod'
import {
  GroupThemeSchema,
  GroupSettingsAPISchema,
  GroupPermissionsSchema,
  GroupSchoolRefSchema,
  GroupMemberTypeSchema,
  NotificationLevelSchema
} from './common'

// ============================================================================
// GET /v1/groups/:groupId
// ============================================================================

export const GetGroupRequestSchema = z.object({
  groupPublicId: z.string(),
  schoolId: z.string().optional(),
  includeFeedStats: z.boolean().optional()
})

export type GetGroupRequest = z.infer<typeof GetGroupRequestSchema>

// Mirrors the Prisma `StreamChatSyncStatus` enum. Exposed on group-detail so
// mobile can gate the Spaces chat icon on provisioning status (SYNCED) and
// route to the canonical channel id (SCH-180 / plan U7). `DISABLED` is part of
// the underlying enum, so it is accepted here too even though the gate keys on
// `SYNCED`.
export const StreamChatStatusSchema = z.enum(['PENDING', 'SYNCED', 'FAILED', 'DISABLED'])

export type StreamChatStatus = z.infer<typeof StreamChatStatusSchema>

export const GroupDefaultNotificationsSchema = z.object({
  showInTimeline: z.boolean(),
  notifyLevel: NotificationLevelSchema,
  notifyFeed: z.boolean(),
  notifyPush: z.boolean(),
  notifyEmail: z.boolean(),
  notifySms: z.boolean(),
  showInCommunity: z.boolean()
})

export const GroupSyncSchema = z.object({
  sourceSyncEnabled: z.boolean(),
  canBeSynced: z.boolean(),
  lastSyncedAt: z.union([z.string(), z.date()]).nullable()
})

export const FeedStatsSchema = z
  .object({
    feedId: z.string(),
    feedGroup: z.string(),
    followers: z.object({
      count: z.number(),
      list: z.array(z.any())
    }),
    following: z.object({
      count: z.number(),
      list: z.array(z.any())
    })
  })
  .nullable()
  .optional()

export const GroupDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  displayName: z.string(),
  displayDescription: z.string().nullable(),
  status: z.string(),
  audience: z.string(),
  source: z.string(),
  groupType: z.string(),
  isSystemGroup: z.boolean(),
  showInApp: z.boolean().optional(),
  betaTestersOnly: z.boolean().optional(),
  mediaLibraryEnabled: z.boolean().optional(),
  // Stream Chat channel pointer + provisioning status (SCH-180 / plan U7).
  // Optional + nullable so unprovisioned groups and stale clients parse cleanly.
  // Mobile gates the Spaces chat icon on `streamChatStatus === 'SYNCED'` and
  // routes to the canonical `streamChatChannelId`. Web type-consumes but does
  // not read these fields.
  streamChatChannelId: z.string().nullable().optional(),
  streamChatStatus: StreamChatStatusSchema.nullable().optional(),
  memberCount: z.number(),
  theme: GroupThemeSchema,
  settings: GroupSettingsAPISchema,
  permissions: GroupPermissionsSchema,
  school: GroupSchoolRefSchema,
  section: z.object({ offeringType: z.string().nullable() }).nullable(),
  defaultNotifications: GroupDefaultNotificationsSchema,
  sync: GroupSyncSchema,
  feedStats: FeedStatsSchema,
  members: z.array(GroupMemberTypeSchema).nullable(),
  myMembership: GroupMemberTypeSchema.nullable(),
  contactEnabled: z.boolean().optional(),
  awaitingStaffSince: z.string().nullable().optional(),
  contactTargetGroup: z
    .object({
      id: z.string(),
      name: z.string(),
      displayName: z.string().nullable().optional(),
      avatarImageUrl: z.string().nullable().optional()
    })
    .nullable()
    .optional(),
  // Announcement channel fields (see list.ts for semantics).
  announcementMode: z.boolean().optional(),
  announcementOriginGroup: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .nullable()
    .optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()])
})

export type GroupDetail = z.infer<typeof GroupDetailSchema>

export const GetGroupResponseSchema = z.object({
  data: GroupDetailSchema,
  message: z.string()
})

export type GetGroupResponse = z.infer<typeof GetGroupResponseSchema>
